/**
 * Aspect — SitSystem (sit on chairs/sofas/toilets, lay on beds)
 * --------------------------------------------------------------
 * Walk near a sittable piece and press E (desktop) or tap the Interact button
 * (touch) to sit down; the camera settles onto the seat with mouse-look still
 * live. Press E again (or tap Stand) to get up exactly where you were.
 *
 * Emits:
 *   sit:candidate { id, name, verb }  — nearest sittable within reach (or null)
 *   sit:state     { seated, verb }    — entering/leaving a seat
 */

const REACH = 2.3; // meters from camera to seat center
const SEATED_EYE_ABOVE_SEAT = 0.62;
const LAY_EYE_ABOVE_TOP = 0.18;

// Cinematic transition timings (seconds).
const DUR_SIT = 0.9;
const DUR_STAND = 0.72;
const DUR_LAY = 1.25;
const DUR_RISE = 1.0;

/** Smooth ease-in-out (cubic). */
const easeInOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

/** Gentle overshoot — used on the Y axis when settling into a mattress. */
const easeSettleY = (t) => {
  const s = 1.18;
  const u = t - 1;
  return 1 + u * u * ((s + 1) * u + s);
};

/** Signed shortest yaw delta a→b in degrees. */
const yawDelta = (a, b) => {
  let d = (b - a) % 360;
  if (d > 180) d -= 360;
  if (d < -180) d += 360;
  return d;
};

/** verb for an asset id: "sit" | "lay" | null */
export function sitVerbFor(assetId) {
  const id = String(assetId ?? "").toLowerCase();
  if (!id) return null;
  if (/bed_pillow|bedside|toy_|_kid/.test(id)) return null;
  if (/(^|_)bed(_|$)|bed_bunk|bed_single/.test(id)) return "lay";
  if (/chair|armchair|sofa|couch|stool|bench|toilet/.test(id)) return "sit";
  return null;
}

export class SitSystem {
  /**
   * @param {import('playcanvas').AppBase} app
   * @param {import('playcanvas').Entity} camera
   * @param {import('../scene_manager.js').SceneManager} scene
   * @param {import('./first_person_controls.js').FirstPersonControls} fp
   * @param {{ gizmo?: object, placement?: object }} [opts]
   */
  constructor(app, camera, scene, fp, opts = {}) {
    this.app = app;
    this.camera = camera;
    this.scene = scene;
    this.fp = fp;
    this.gizmo = opts.gizmo ?? null;
    this.placement = opts.placement ?? null;

    this.seatedOn = null; // placement id while seated
    this._verb = null;
    this._preSit = null; // camera pose to restore on stand
    this._candidate = null;
    this._acc = 0;
    /** Live camera tween: { t, dur, from, to, dip, settleY, onDone } */
    this._anim = null;

    this.app.on("update", (dt) => {
      this._animTick(dt);
      this._tick(dt);
    });
    this.app.on("ui:interact", () => this.toggle());
    this.app.on("scene:room-loaded", () => {
      // Room rebuilt — the seat entity is gone; quietly stand up.
      if (this.seatedOn) this._standInstant();
    });
    window.addEventListener("keydown", (e) => {
      if (e.key !== "e" && e.key !== "E") return;
      if (this._typing()) return;
      if (document.body.classList.contains("plan-editor-open")) return;
      if (this.gizmo?.selectedId || this.placement?.active) return;
      this.toggle();
    });
  }

  _typing() {
    const el = document.activeElement;
    const tag = el?.tagName?.toLowerCase();
    return tag === "input" || tag === "textarea" || tag === "select" || el?.isContentEditable;
  }

  /** Nearest sittable placement within reach — and in the SAME room (a bed
   *  behind the bathroom wall must never win over the toilet next to you). */
  _findCandidate() {
    if (!this.fp?.enabled) return null;
    const cam = this.camera.getPosition();
    const camRoom = this.scene.roomAt?.(cam.x, cam.z)?.name ?? null;
    let best = null;
    let bestD = REACH;
    for (const p of this.scene.placements?.values?.() ?? []) {
      const verb = sitVerbFor(p.asset_id);
      if (!verb) continue;
      const d = Math.hypot(p.position[0] - cam.x, p.position[2] - cam.z);
      if (d >= bestD) continue;
      const itemRoom = this.scene.roomAt?.(p.position[0], p.position[2])?.name ?? null;
      if (itemRoom !== camRoom) continue;
      bestD = d;
      best = { id: p.id, verb };
    }
    return best;
  }

  _tick(dt) {
    this._acc += dt ?? 0.016;
    if (this._acc < 0.2) return;
    this._acc = 0;

    if (this.seatedOn) {
      // Seat sold from under us (deleted / moved far) -> stand.
      if (!this.scene.placements?.get?.(this.seatedOn)) this._standInstant();
      return;
    }
    const next = this._findCandidate();
    const changed = (next?.id ?? null) !== (this._candidate?.id ?? null);
    this._candidate = next;
    if (changed) {
      const rec = next ? this.scene.placements.get(next.id) : null;
      const meta = rec ? this.scene.catalog?.get?.(rec.asset_id) : null;
      this.app.fire("sit:candidate", next ? { id: next.id, name: meta?.name ?? rec?.asset_id, verb: next.verb } : null);
    }
  }

  toggle() {
    if (this._anim) return; // let the current transition finish
    if (this.seatedOn) this.stand();
    else this.sit();
  }

  /**
   * Advance the live camera tween: eased XZ glide, optional mattress-settle on
   * Y, shortest-path yaw sweep, and a mid-flight pitch dip (you glance at the
   * seat while lowering, then level out) — all driven through fp.seatPose so
   * walking stays locked for the whole transition.
   */
  _animTick(dt) {
    const a = this._anim;
    if (!a) return;
    a.t += dt ?? 0.016;
    const t = Math.min(1, a.t / a.dur);
    const e = easeInOut(t);
    const ey = a.settleY ? easeSettleY(t) : e;

    this.fp.seatPose = {
      x: a.from.x + (a.to.x - a.from.x) * e,
      y: a.from.y + (a.to.y - a.from.y) * ey,
      z: a.from.z + (a.to.z - a.from.z) * e,
    };
    this.fp.yaw = a.from.yaw + yawDelta(a.from.yaw, a.to.yaw) * e;
    this.fp.pitch =
      a.from.pitch + (a.to.pitch - a.from.pitch) * e + (a.dip ?? 0) * Math.sin(Math.PI * e);
    this.fp._applyRotation?.();

    if (t >= 1) {
      this._anim = null;
      a.onDone?.();
    }
  }

  /** Start a camera tween (movement stays pinned via fp.seatPose). */
  _animate(from, to, { dur, dip = 0, settleY = false, onDone } = {}) {
    this.fp.seatPose = { x: from.x, y: from.y, z: from.z };
    this._anim = { t: 0, dur: dur ?? 0.8, from, to, dip, settleY, onDone };
  }

  sit() {
    const cand = this._candidate ?? this._findCandidate();
    if (!cand || this.seatedOn || this._anim) return;
    const rec = this.scene.placements.get(cand.id);
    if (!rec) return;
    const meta = this.scene.catalog?.get?.(rec.asset_id);
    const fp = rec.footprint ?? meta?.footprint ?? { w: 0.6, h: 0.5, d: 0.6 };
    const scale = rec.scale ?? 1;
    const h = (fp.h ?? 0.5) * scale;
    const baseY = rec.position[1];

    const camPos = this.camera.getPosition();
    this._preSit = { x: camPos.x, y: camPos.y, z: camPos.z, yaw: this.fp.yaw, pitch: this.fp.pitch };

    const lay = cand.verb === "lay";
    const eyeY = lay
      ? baseY + h + LAY_EYE_ABOVE_TOP
      : baseY + Math.min(0.55, h * 0.55) + SEATED_EYE_ABOVE_SEAT;
    // Face the way the furniture faces (chair forward = away from the backrest).
    const yaw = ((rec.rotation_y ?? 0) + 180) % 360;
    const pitch = lay ? 58 : -4; // laying = gaze up toward the ceiling

    this.seatedOn = cand.id;
    this._verb = cand.verb;
    this.app.fire("sit:state", { seated: true, verb: cand.verb });

    this._animate(
      { x: camPos.x, y: camPos.y, z: camPos.z, yaw: this.fp.yaw, pitch: this.fp.pitch },
      { x: rec.position[0], y: eyeY, z: rec.position[2], yaw, pitch },
      lay
        ? { dur: DUR_LAY, settleY: true } // slow lie-back with a mattress settle
        : { dur: DUR_SIT, dip: -10 }, // glance at the seat while lowering
    );
  }

  stand() {
    if (!this.seatedOn || this._anim) return;
    const pose = this._preSit;
    const wasLay = this._verb === "lay";
    this.seatedOn = null;
    this._verb = null;
    this.app.fire("sit:state", { seated: false, verb: null });

    if (!pose) {
      this.fp.seatPose = null;
      this._preSit = null;
      return;
    }

    const cam = this.camera.getPosition();
    this._animate(
      { x: cam.x, y: cam.y, z: cam.z, yaw: this.fp.yaw, pitch: this.fp.pitch },
      { x: pose.x, y: pose.y, z: pose.z, yaw: pose.yaw, pitch: pose.pitch },
      {
        // Rising from a bed is a slower sit-up; from a chair a brisk push-up.
        dur: wasLay ? DUR_RISE : DUR_STAND,
        dip: wasLay ? -8 : 4, // beds: curl forward as you sit up; chairs: tiny lift
        onDone: () => {
          this.fp.seatPose = null;
          this.fp.yaw = pose.yaw;
          this.fp.pitch = pose.pitch;
          this.fp._applyRotation?.();
          this._preSit = null;
        },
      },
    );
  }

  _standInstant() {
    this._anim = null;
    this.fp.seatPose = null;
    this.seatedOn = null;
    this._verb = null;
    this._preSit = null;
    this.app.fire("sit:state", { seated: false, verb: null });
  }
}

export default SitSystem;
